import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './layouts/AppLayout';
import HoumaoWorkspaceRoute, { PreserveContextRedirect } from './layouts/HoumaoWorkspaceRoute';
import UploadPage from './pages/UploadPage';
import MacroDashboard from './pages/MacroDashboard';
import MicroRoutingPage from './pages/MicroRoutingPage';
import StationAlertPage from './pages/StationAlertPage';
import ExportPage from './pages/ExportPage';
import AgentChatRoutePage from './pages/AgentChatRoutePage';
import AdminRouteGuard from './components/admin/AdminRouteGuard';
import { useThemeEffect } from './hooks/useThemeEffect';
import AdminConsolePage from './pages/AdminConsolePage';
import AdminLoginPage from './pages/AdminLoginPage';
import AvomanCalibrationReviewPage from './pages/AvomanCalibrationReviewPage';
import WechatPermanentEntryPage from './pages/WechatPermanentEntryPage';
import NiuboshiExpertSharePage from './pages/NiuboshiExpertSharePage';
import HoumaoAgentLandingPage from './pages/HoumaoAgentLandingPage';
import HoumaoExpertSquarePage from './pages/HoumaoExpertSquarePage';
import AvatarFactoryPage from './pages/AvatarFactoryPage';
import HoumaoExpertRedirectPage from './pages/HoumaoExpertRedirectPage';
import MenglianDataUploadPage from './pages/MenglianDataUploadPage';
import DailyReportPage from './pages/DailyReportPage';
import PaymentPage from './pages/PaymentPage';
import AlipayReturnPage from './pages/AlipayReturnPage';
import LegalAgreementPage from './pages/LegalAgreementPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import ProjectShareClaimPage from './pages/ProjectShareClaimPage';
import { CONSUMER_ACCOUNT_PAGES } from './lib/consumer-account-routes';
import { pendingAlipayReturnUrl } from './lib/payment-methods';
import AlipayReturnCoordinator from './components/payments/AlipayReturnCoordinator';
import PublicCanonicalLink from './components/seo/PublicCanonicalLink';
import LuyouWorkbookTemplatePage from './pages/LuyouWorkbookTemplatePage';
import GlobalDesignStudioRoutePage from './pages/GlobalDesignStudioRoutePage';
import BrowserPluginPage from './pages/BrowserPluginPage';
import BrowserAgentAuthorizePage from './pages/BrowserAgentAuthorizePage';
import BrowserAgentDiagnosticPage from './pages/BrowserAgentDiagnosticPage';

interface AppProps {
  locationHostname?: string;
}

const HOUMAO_AGENT_HOSTS = ['houmao.com', 'www.houmao.com'];

export default function App({ locationHostname }: AppProps = {}) {
  useThemeEffect();
  useEffect(() => {
    const recoverAlipayRedirect = () => {
      const returnUrl = pendingAlipayReturnUrl(
        window.location.pathname,
        window.location.search,
        window.location.hash,
      );
      if (returnUrl) window.location.replace(returnUrl);
    };
    recoverAlipayRedirect();
    window.addEventListener('pageshow', recoverAlipayRedirect);
    return () => window.removeEventListener('pageshow', recoverAlipayRedirect);
  }, []);
  const hostname =
    locationHostname
    ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const isHoumaoAgentHost =
    HOUMAO_AGENT_HOSTS.includes(hostname.toLowerCase());

  return (
    <>
      <PublicCanonicalLink />
      <AlipayReturnCoordinator />
      <Routes>
      <Route path="/browser-plugin" element={<BrowserPluginPage />} />
      <Route path="/browser-agent/authorize" element={<BrowserAgentAuthorizePage />} />
      <Route path="/browser-agent/diagnostic" element={<BrowserAgentDiagnosticPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/*" element={<AdminRouteGuard />}>
        <Route index element={<AdminConsolePage />} />
        <Route path="avoman-calibration" element={<AvomanCalibrationReviewPage />} />
        <Route path="avatar-factory" element={<AvatarFactoryPage />} />
        <Route path="daily-report" element={<DailyReportPage />} />
        <Route path="luyou-workbook-template" element={<LuyouWorkbookTemplatePage />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
      <Route path="/wechat/avatar-entry/:publicToken" element={<WechatPermanentEntryPage />} />
      <Route path="/wechat/expert-share/:expertSlug" element={<NiuboshiExpertSharePage />} />
      <Route path="/legal/:agreementId" element={<LegalAgreementPage />} />
      <Route path="/r/:token" element={<ReferralLandingPage />} />
      <Route path="/mengliandata" element={<MenglianDataUploadPage />} />
      <Route path="/daily-report" element={<Navigate to="/admin/daily-report" replace />} />
      {/* Independent callbacks and legacy operations do not normalize workspace. */}
      <Route element={<AppLayout />}>
        <Route path="/pay/alipay/return" element={<AlipayReturnPage />} />
        {!isHoumaoAgentHost && <>
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/dashboard" element={<MacroDashboard />} />
          <Route path="/routing" element={<MicroRoutingPage />} />
          <Route path="/station-alert" element={<StationAlertPage />} />
          <Route path="/export" element={<ExportPage />} />
        </>}
      </Route>
      {/* Add consumer pages here so workspace normalization is inherited. */}
      <Route element={<HoumaoWorkspaceRoute />}>
        <Route path="/project-shares" element={<ProjectShareClaimPage />} />
        <Route path="/houmao" element={<PreserveContextRedirect to="/" />} />
        <Route path="/houmao_v2" element={<PreserveContextRedirect to="/" />} />
        <Route path="/experts" element={<HoumaoExpertSquarePage />} />
        <Route path="/af/*" element={<AvatarFactoryPage />} />
        <Route path="/avatar-factory/*" element={<AvatarFactoryPage />} />
        <Route path="/" element={<HoumaoAgentLandingPage />} />
        <Route element={<AppLayout />}>
          <Route path="/chat" element={<AgentChatRoutePage />} />
          <Route path="/design-studio" element={<GlobalDesignStudioRoutePage />} />
          <Route path="/ai-chat" element={<PreserveContextRedirect to="/chat" />} />
          {CONSUMER_ACCOUNT_PAGES.map(({ path, component: Page }) => (
            <Route key={path} path={`/${path}`} element={<Page />} />
          ))}
          <Route path="/pay" element={<PaymentPage />} />
          <Route path="/pay/wechat" element={<PreserveContextRedirect to="/pay" />} />
        </Route>
        {isHoumaoAgentHost && <Route path="/:expertSlug" element={<HoumaoExpertRedirectPage />} />}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
